import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import { PatientSlot } from '@/components/pages/kiosk/PACUv2/index';
import Button from '@material-ui/core/Button';
import { assignBedInRoom, listOrPacuPatients } from '@/graph/patients';
import { useMutation, useSubscription } from '@apollo/client';
import { makeStyles } from '@material-ui/core/styles';

export interface ConfirmationDialogRawProps {
  id: string;
  keepMounted: boolean;
  value: string;
  bedNumber: number;
  open: boolean;
  onAssigned: (patientId: any, roomId: any) => void;
  onClose: (value?: string) => void;
  roomId: any;
}

function SelectPatientDialog(props: ConfirmationDialogRawProps) {
  const { onAssigned, onClose, value: valueProp, open, ...other } = props;
  const classes = useStyles();
  const [value, setValue] = React.useState(valueProp);
  const radioGroupRef = React.useRef<HTMLElement>(null);
  const query = useSubscription(listOrPacuPatients);

  React.useEffect(() => {
    if (!open) {
      setValue(valueProp);
    }
  }, [valueProp, open]);

  const handleEntering = () => {
    if (radioGroupRef.current != null) {
      radioGroupRef.current.focus();
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleOk = () => {
    onClose(value);
  };

  const [assignBedMutation] = useMutation(assignBedInRoom);

  const handleChange = async patientId => {
    try {
      await assignBedMutation({
        variables: {
          id: patientId,
          bed: `${props.bedNumber}`,
          roomId: props.roomId,
        },
      });

      onAssigned(patientId, props.roomId);

      onClose();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Dialog
      maxWidth="md"
      TransitionProps={{
        onEnter: handleEntering,
      }}
      aria-labelledby="confirmation-dialog-title"
      open={open}
      classes={{
        paper: classes.dialogPaper,
      }}
    >
      <DialogTitle id="confirmation-dialog-title">
        Assign patient to bed {parseInt(String(props.bedNumber), 10)}
      </DialogTitle>
      <DialogContent dividers>
        <Box className={classes.patientList}>
          {(query.data?.patients || []).map(patient => (
            <PatientSlot
              key={patient.id}
              bedNumber={patient.pacuBed}
              patientName={patient.name}
              surgeonName={patient?.physician?.name}
              procedure={patient?.procedureType?.name}
              pacuNurse={patient?.pacuNurse}
              inside={props.roomId === patient?.room?.id}
              onClick={() => handleChange(patient.id)}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleOk} color="primary">
          Ok
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    width: '80%',
  },
  patientList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
}));

export default SelectPatientDialog;
